/**
 * ScheduleSection — workshop detail "Schedule" (BON-117): Date, Start, End.
 * PRESENTATIONAL only — the page owns the consolidated edit mode
 * (useWorkshopEditForm); this renders the read grid or the form inputs based on
 * `editing`, with no per-section Edit/Save.
 *
 * The fields are typed or picked (DateField / TimeField, round-tripping the
 * same YYYY-MM-DD and HH:MM strings); the page validates "End must be after
 * start." and highlights the offending input.
 */
import type { WorkshopDetail } from '../../../../api/workshops';
import { DateField } from '../../primitives/DateField';
import { TimeField } from '../../primitives/TimeField';
import { ReadGrid } from '../../venues/detail/ReadGrid';
import { Field } from '../../venues/venueCreateFields';

import type { WorkshopEditDraft, WorkshopFieldErrors } from './useWorkshopEditForm';
import { formatDate, formatTime } from './workshopFields';

interface Props {
  workshop: WorkshopDetail;
  editing: boolean;
  draft: WorkshopEditDraft;
  errors: WorkshopFieldErrors;
  setField: (key: keyof WorkshopEditDraft, value: string) => void;
}

export function ScheduleSection({ workshop, editing, draft, errors, setField }: Props) {
  if (!editing) {
    return (
      <ReadGrid
        cols={3}
        rows={[
          { label: 'Date', value: formatDate(workshop.localStartDatetime) },
          { label: 'Start', value: formatTime(workshop.localStartDatetime) },
          { label: 'End', value: formatTime(workshop.localEndDatetime) },
        ]}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
      <Field label="Date" required error={errors.date}>
        <DateField
          ariaLabel="Date"
          value={draft.date}
          error={!!errors.date}
          onChange={(v) => setField('date', v)}
        />
      </Field>
      <Field label="Start" required error={errors.start}>
        <TimeField
          ariaLabel="Start"
          value={draft.start}
          error={!!errors.start}
          onChange={(v) => setField('start', v)}
        />
      </Field>
      <Field label="End" required error={errors.end}>
        <TimeField
          ariaLabel="End"
          value={draft.end}
          error={!!errors.end}
          onChange={(v) => setField('end', v)}
        />
      </Field>
    </div>
  );
}
